import React, { useState, useEffect, useRef } from 'react';
import { ArrowRight } from 'lucide-react';
import gsap from 'gsap';
import emailjs from '@emailjs/browser';

export const ContactForm: React.FC = () => {
    const formRef = useRef<HTMLDivElement>(null);
    const formElementRef = useRef<HTMLFormElement>(null);
    const [formData, setFormData] = useState({
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        message: ''
    });

    const [submitted, setSubmitted] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [error, setError] = useState<string | null>(null);

    // Initialize EmailJS when component mounts
    useEffect(() => {
        const publicKey = import.meta.env.VITE_EMAILJS_PUBLIC_KEY;

        // Debug: Check if env vars are loaded
        console.log('EmailJS Config Check:', {
            hasPublicKey: !!publicKey,
            hasServiceId: !!import.meta.env.VITE_EMAILJS_SERVICE_ID,
            hasTemplateId: !!import.meta.env.VITE_EMAILJS_TEMPLATE_ID,
        });

        if (publicKey) {
            emailjs.init(publicKey);
            console.log('EmailJS initialized successfully');
        } else {
            console.warn('EmailJS public key not found in environment variables');
        }
    }, []);

    // Entrance animation
    useEffect(() => {
        if (formRef.current) {
            gsap.fromTo(
                formRef.current,
                { opacity: 0, y: 30 },
                {
                    opacity: 1,
                    y: 0,
                    duration: 0.8,
                    ease: 'power3.out',
                    delay: 0.2
                }
            );
        }
    }, []);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        setFormData({ ...formData, [e.target.name]: e.target.value });
        setError(null); // Clear error when user types
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setIsSubmitting(true);
        setError(null);

        try {
            // EmailJS template parameters - recipient configured in EmailJS dashboard
            const templateParams = {
                from_name: `${formData.firstName} ${formData.lastName}`,
                from_email: formData.email,
                from_phone: formData.phone,
                message: formData.message,
                to_email: 'info@creativeyadley.com', // Test recipient
                reply_to: formData.email,
            };

            await emailjs.send(
                import.meta.env.VITE_EMAILJS_SERVICE_ID || '',
                import.meta.env.VITE_EMAILJS_TEMPLATE_ID || '',
                templateParams
            );

            console.log('Email sent successfully');
            setSubmitted(true);

            // Reset form after 5 seconds
            setTimeout(() => {
                setSubmitted(false);
                setFormData({
                    firstName: '',
                    lastName: '',
                    email: '',
                    phone: '',
                    message: ''
                });
            }, 5000);
        } catch (err) {
            console.error('Failed to send email:', err);
            setError('Failed to send message. Please try again or contact us directly.');
        } finally {
            setIsSubmitting(false);
        }
    };

    if (submitted) {
        return (
            <div className="w-full h-full flex items-center justify-center p-8 lg:p-16 bg-[#293039]">
                <div className="text-center max-w-lg">
                    <div className="mb-6">
                        <svg className="w-20 h-20 mx-auto text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                    </div>
                    <h3 className="text-3xl font-serif text-white mb-4">Thank You So Much!</h3>
                    <p className="text-xl text-neutral-300 leading-relaxed">
                        Thanks for reaching out, we'll be in touch soon. Your healing journey matters to us.
                    </p>
                </div>
            </div>
        );
    }

    return (
        <div className="w-full h-full flex items-center justify-center p-8 lg:p-16 pt-20 lg:pt-24 bg-[#293039]">
            <div ref={formRef} className="w-full max-w-xl">
                <form onSubmit={handleSubmit} className="space-y-8">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                        <div className="group">
                            <label htmlFor="firstName" className="block text-xs font-bold uppercase tracking-wider text-white mb-3">
                                First Name *
                            </label>
                            <input
                                type="text"
                                name="firstName"
                                id="firstName"
                                placeholder="FIRST NAME"
                                required
                                value={formData.firstName}
                                onChange={handleChange}
                                className="w-full bg-transparent border-b-2 border-neutral-600 py-3 pl-3 text-white placeholder-neutral-500 focus:border-[#d68800] focus:bg-white focus:text-neutral-900 focus:placeholder-neutral-400 focus:outline-none transition-all duration-300 text-lg"
                            />
                        </div>
                        <div className="group">
                            <label htmlFor="lastName" className="block text-xs font-bold uppercase tracking-wider text-white mb-3">
                                Last Name *
                            </label>
                            <input
                                type="text"
                                name="lastName"
                                id="lastName"
                                placeholder="LAST NAME"
                                required
                                value={formData.lastName}
                                onChange={handleChange}
                                className="w-full bg-transparent border-b-2 border-neutral-600 py-3 pl-3 text-white placeholder-neutral-500 focus:border-[#d68800] focus:bg-white focus:text-neutral-900 focus:placeholder-neutral-400 focus:outline-none transition-all duration-300 text-lg"
                            />
                        </div>
                    </div>

                    {/* Email + Phone side by side */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                        <div className="group">
                            <label htmlFor="email" className="block text-xs font-bold uppercase tracking-wider text-white mb-3">
                                Email *
                            </label>
                            <input
                                type="email"
                                name="email"
                                id="email"
                                placeholder="YOUR EMAIL"
                                required
                                value={formData.email}
                                onChange={handleChange}
                                className="w-full bg-transparent border-b-2 border-neutral-600 py-3 pl-3 text-white placeholder-neutral-500 focus:border-[#d68800] focus:bg-white focus:text-neutral-900 focus:placeholder-neutral-400 focus:outline-none transition-all duration-300 text-lg"
                            />
                        </div>
                        <div className="group">
                            <label htmlFor="phone" className="block text-xs font-bold uppercase tracking-wider text-white mb-3">
                                Phone *
                            </label>
                            <input
                                type="tel"
                                name="phone"
                                id="phone"
                                placeholder="YOUR PHONE"
                                required
                                value={formData.phone}
                                onChange={handleChange}
                                className="w-full bg-transparent border-b-2 border-neutral-600 py-3 pl-3 text-white placeholder-neutral-500 focus:border-[#d68800] focus:bg-white focus:text-neutral-900 focus:placeholder-neutral-400 focus:outline-none transition-all duration-300 text-lg"
                            />
                        </div>
                    </div>

                    <div className="group">
                        <label htmlFor="message" className="block text-xs font-bold uppercase tracking-wider text-white mb-3">
                            How can we help? *
                        </label>
                        <textarea
                            name="message"
                            id="message"
                            placeholder="PLEASE LET US KNOW HOW WE CAN ASSIST"
                            required
                            rows={5}
                            value={formData.message}
                            onChange={handleChange}
                            className="w-full bg-transparent border-b-2 border-neutral-600 py-3 pl-3 text-white placeholder-neutral-500 focus:border-[#d68800] focus:bg-white focus:text-neutral-900 focus:placeholder-neutral-400 focus:outline-none transition-all duration-300 resize-none text-lg"
                        ></textarea>
                    </div>

                    {/* Error Message */}
                    {error && (
                        <div className="p-4 bg-red-500/20 border border-red-500/50 rounded-lg">
                            <p className="text-red-300 text-sm">{error}</p>
                        </div>
                    )}

                    <div className="pt-6">
                        <button
                            type="submit"
                            disabled={isSubmitting}
                            className={`group flex items-center gap-4 text-sm font-bold uppercase tracking-widest transition-all duration-300 ${isSubmitting
                                ? 'text-neutral-500 cursor-not-allowed'
                                : 'text-white hover:text-[#891719]'
                                }`}
                        >
                            {isSubmitting ? 'Sending...' : 'Send Message'}
                            <span className={`inline-flex items-center justify-center w-12 h-12 rounded-full text-white transition-all duration-300 ${isSubmitting
                                ? 'bg-neutral-600'
                                : 'bg-[#d68800] group-hover:bg-[#891719] group-hover:translate-x-1 group-hover:scale-110'
                                }`}>
                                {isSubmitting ? (
                                    <svg className="animate-spin w-5 h-5" fill="none" viewBox="0 0 24 24">
                                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                    </svg>
                                ) : (
                                    <ArrowRight size={20} />
                                )}
                            </span>
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};
